import React from 'react';
import { AutoGrid, Checkbox, ControlledFormInput, TextField } from 'c-components';
import type { Campaign } from 'c-sdk';
import { PermissionName, UserType } from 'c-sdk';
import { Box } from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import type { TranslationPath } from 'c-translation';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useAuthenticatedUser, useUrlState, useUrlStateValue } from 'c-hooks';
import type { PCAUrlState } from 'c-main/Components/Campaign/CampaignReports/types';
import { PCAMetricUrlStateKeys } from 'c-main/Components/Campaign/CampaignReports/types';
import { usesInsertionOrderTargeting } from 'c-main/Lib';
import { useAtom } from 'jotai';
import { atom_useLinearReportingOverride } from '../atoms';

type Props = {
    campaign: Pick<Campaign, 'type' | 'expected_impacts'>;
    campaignHasDspReporting: boolean;
};

const derivedFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/**
 * The Reporting Inputs form (moved out of `DialogsContainer`).
 *
 * UL-8133 -- on an insertion-order targeting campaign (`vistar_us2`) Expected Impressions is the
 * sum of the insertion order targets typed on the schedule page. It is shown read-only and is not
 * a form field at all, so it is never sent (the API refuses it with a 422). See
 * `reportingInputDefaults`, which leaves it out of the form's values for the same reason.
 */
const ReportingInputsForm: React.FC<Props> = ({ campaign, campaignHasDspReporting }) => {
    const t = useCommonTranslation();
    const { chosenUserType } = useAuthenticatedUser();
    const isMediaOwner = chosenUserType === UserType.Owner;
    const [useLinearReportingOverride, setUseLinearReportingOverride] = useAtom(
        atom_useLinearReportingOverride,
    );
    const { pushMany } = useUrlState();
    const urlFiltersValue = useUrlStateValue<PCAUrlState['filters']>(
        PCAMetricUrlStateKeys.filters,
        {},
    );
    const expectedIsDerived = usesInsertionOrderTargeting(campaign);

    const makeNumberField = (labelKey: TranslationPath) =>
        function NumberField({ field }: { field: any }) {
            return (
                <TextField
                    {...field}
                    label={t(labelKey)}
                    type="number"
                    inputProps={{ step: 'any', min: 0 }}
                    value={field.value ?? ''}
                    onChange={e => {
                        const v = e.target.value;
                        field.onChange(v === '' ? null : Number(v));
                    }}
                    fullWidth
                />
            );
        };

    return (
        <AutoGrid spacing={2} xs={12}>
            <IfHasAllPermissions permissions={[PermissionName.AdminCampaignEdit_expected_impacts]}>
                {expectedIsDerived ? (
                    <TextField
                        label={t('Modules.Main.Campaigns.Overview.OverviewSection.expectedImpacts')}
                        value={
                            campaign.expected_impacts == null
                                ? ''
                                : derivedFormatter.format(Number(campaign.expected_impacts))
                        }
                        helperText={t(
                            'Modules.Main.Campaigns.Overview.OverviewSection.expectedImpactsDerived',
                        )}
                        InputProps={{ readOnly: true }}
                        fullWidth
                    />
                ) : (
                    <ControlledFormInput
                        name="expected_impacts"
                        render={makeNumberField(
                            'Modules.Main.Campaigns.Overview.OverviewSection.expectedImpacts',
                        )}
                    />
                )}
            </IfHasAllPermissions>

            <IfHasAllPermissions permissions={[PermissionName.AdminCampaignEdit_bought_value]}>
                <ControlledFormInput
                    name="bought_value"
                    render={makeNumberField(
                        'Modules.Main.Campaigns.Overview.OverviewSection.boughtValue',
                    )}
                />
            </IfHasAllPermissions>

            <IfHasAllPermissions permissions={[PermissionName.AdminCampaignEdit_delivered_value]}>
                <ControlledFormInput
                    name="delivered_value"
                    render={makeNumberField(
                        'Modules.Main.Campaigns.Overview.OverviewSection.deliveredValue',
                    )}
                />
            </IfHasAllPermissions>
            {!isMediaOwner && (
                <Box display="flex" justifyContent="center">
                    <ControlledFormInput
                        name="show_campaign_values_box"
                        render={({ field }) => (
                            <Checkbox
                                isBoolean
                                label="Show Value"
                                checked={Boolean(field.value)}
                                onChange={value => field.onChange(Boolean(value))}
                            />
                        )}
                    />
                </Box>
            )}
            {campaignHasDspReporting && (
                <IfHasAllPermissions
                    permissions={[PermissionName.AdminCampaignUse_linear_reporting]}
                >
                    <Box display="flex" justifyContent="center">
                        <Checkbox
                            isBoolean
                            label="Use Linear Reporting"
                            checked={useLinearReportingOverride}
                            onChange={value => {
                                const nextValue = Boolean(value);
                                setUseLinearReportingOverride(nextValue);

                                // Reset the metric tab on switch — DSP metrics (analysis) and
                                // linear metrics (display/time) don't overlap, so keep it on the
                                // shared "summary" tab to avoid landing on a non-existent tab.
                                const updates: Record<string, any> = { metric: 'summary' };

                                if (nextValue) {
                                    const nextFilters = {
                                        ...(urlFiltersValue ?? {}),
                                    } as Record<string, any[]>;
                                    delete nextFilters.audience_display_name;
                                    delete nextFilters.audience_version;
                                    updates[PCAMetricUrlStateKeys.filters] = nextFilters;
                                }

                                pushMany(updates);
                            }}
                        />
                    </Box>
                </IfHasAllPermissions>
            )}
        </AutoGrid>
    );
};

export default ReportingInputsForm;
